import React, { useState, useEffect } from "react";
import {
  CheckSquare,
  Plus,
  Edit2,
  Trash2,
  Search,
  X,
  Loader2,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Calendar,
  FileText,
  Link,
  ToggleLeft,
  ToggleRight,
  Eye,
  Shield,
  Users,
  Clock,
  AlertCircle,
  Info,
  Database,
  Hand,
  Lightbulb,
  Target,
  History,
} from "lucide-react";
import { supabase } from "../lib/supabase";

// ============================================
// CONSTANTES LOI 25 - CONSENTEMENTS (Art. 14)
// ============================================

const TYPES_CONSENTEMENT = [
  {
    id: "marketing",
    label: "Communications marketing",
    desc: "Envoi de promotions, infolettres",
  },
  {
    id: "cookies_essentiels",
    label: "Cookies essentiels",
    desc: "Nécessaires au fonctionnement du site",
  },
  {
    id: "cookies_analytics",
    label: "Cookies analytiques",
    desc: "Mesure d'audience et statistiques",
  },
  {
    id: "cookies_publicitaires",
    label: "Cookies publicitaires",
    desc: "Publicité ciblée",
  },
  {
    id: "partage_tiers",
    label: "Partage avec des tiers",
    desc: "Communication à des partenaires",
  },
  {
    id: "profilage",
    label: "Profilage",
    desc: "Analyse comportementale et recommandations",
  },
  {
    id: "geolocalisation",
    label: "Géolocalisation",
    desc: "Utilisation des données de position",
  },
  {
    id: "transfert_hors_qc",
    label: "Transfert hors Québec",
    desc: "Communication à l'extérieur du Québec",
  },
  {
    id: "donnees_sensibles",
    label: "Données sensibles",
    desc: "Santé, biométrie, opinions, etc.",
  },
  {
    id: "decision_automatisee",
    label: "Décision automatisée",
    desc: "Traitement automatisé ayant un impact",
  },
  {
    id: "conservation_prolongee",
    label: "Conservation prolongée",
    desc: "Au-delà de la durée normale",
  },
  { id: "autre", label: "Autre finalité", desc: "Autre type de consentement" },
];

const CATEGORIES_DONNEES = [
  "Nom et coordonnées",
  "Adresse courriel",
  "Numéro de téléphone",
  "Adresse postale",
  "Date de naissance",
  "Préférences et intérêts",
  "Historique d'achats",
  "Données de navigation",
  "Adresse IP",
  "Géolocalisation",
  "Photos / Vidéos",
  "Données de santé",
  "Données biométriques",
  "Opinions politiques",
  "Données financières",
];

const METHODES_OBTENTION = [
  { id: "formulaire_web", label: "Formulaire en ligne", icone: "💻" },
  { id: "case_cocher", label: "Case à cocher", icone: "☑️" },
  { id: "double_optin", label: "Double opt-in (courriel)", icone: "📧" },
  { id: "signature_papier", label: "Signature papier", icone: "✍️" },
  {
    id: "signature_electronique",
    label: "Signature électronique",
    icone: "🔏",
  },
  {
    id: "consentement_verbal",
    label: "Consentement verbal enregistré",
    icone: "🎤",
  },
  { id: "bandeau_cookies", label: "Bandeau de cookies", icone: "🍪" },
  { id: "contrat", label: "Clause contractuelle", icone: "📄" },
];

const MECANISMES_RETRAIT = [
  "Lien de désabonnement dans chaque courriel",
  "Formulaire de retrait sur le site web",
  "Demande par courriel au responsable",
  "Centre de préférences utilisateur",
  "Téléphone au service client",
  "Paramètres du compte utilisateur",
  "Gestionnaire de cookies",
];

// ============================================
// FONCTIONS SUPABASE
// ============================================

const loadConsentementsFromDB = async (entrepriseId) => {
  const { data, error } = await supabase
    .from("consentements")
    .select("*")
    .eq("entreprise_id", entrepriseId)
    .order("created_at", { ascending: false });

  if (error) throw error;
  return data || [];
};

const addConsentementToDB = async (consentementData) => {
  const { data, error } = await supabase
    .from("consentements")
    .insert([consentementData])
    .select()
    .single();

  if (error) throw error;
  return data;
};

const updateConsentementInDB = async (id, updates) => {
  const { data, error } = await supabase
    .from("consentements")
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq("id", id)
    .select()
    .single();

  if (error) throw error;
  return data;
};

const deleteConsentementFromDB = async (id) => {
  const { error } = await supabase.from("consentements").delete().eq("id", id);
  if (error) throw error;
  return true;
};

// ============================================
// COMPOSANT PRINCIPAL
// ============================================

const ConsentementsManager = ({ showToast, entrepriseId }) => {
  const [consentements, setConsentements] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterActif, setFilterActif] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [showModal, setShowModal] = useState(false);
  const [editingConsentement, setEditingConsentement] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [showDetail, setShowDetail] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  // ============================================
  // FORMULAIRE COMPLET LOI 25 (Art. 14)
  // ============================================
  const initialFormData = {
    // IDENTIFICATION
    nom: "",
    type_consentement: "marketing",
    description: "",

    // FINALITÉ (Art. 14 - doit être spécifique)
    finalite_principale: "",
    finalites_secondaires: "",

    // DONNÉES CONCERNÉES
    categories_donnees: [],
    donnees_sensibles: false,
    description_donnees: "",

    // CARACTÉRISTIQUES DU CONSENTEMENT (Art. 14)
    consentement_explicite: false,
    consentement_separe: false, // Séparé des autres conditions
    consentement_granulaire: false, // Peut être donné par finalité

    // INFORMATION FOURNIE (Art. 8)
    info_finalites: true,
    info_categories_donnees: true,
    info_destinataires: false,
    info_duree_conservation: false,
    info_droits: true,
    info_transfert_hors_qc: false,
    texte_information: "",

    // OBTENTION
    methode_obtention: "formulaire_web",
    date_obtention: new Date().toISOString().split("T")[0],
    preuve_consentement: "",
    preuve_url: "",

    // VERSION ET HISTORIQUE
    version: "1.0",
    date_version: new Date().toISOString().split("T")[0],
    changelog: "",

    // RETRAIT
    mecanismes_retrait: [],
    delai_traitement_retrait: "72 heures",

    // VALIDITÉ
    duree_validite: "",
    date_expiration: "",
    renouvellement_requis: false,
    frequence_renouvellement: "",

    // STATUT
    actif: true,
    obligatoire: false,

    // RESPONSABLE
    responsable: "",

    // NOTES
    notes: "",
  };

  const [formData, setFormData] = useState(initialFormData);

  const tabs = [
    { id: 0, title: "Identification", icon: FileText },
    { id: 1, title: "Finalité", icon: Target },
    { id: 2, title: "Données", icon: Database },
    { id: 3, title: "Caractéristiques", icon: CheckSquare },
    { id: 4, title: "Information", icon: Info },
    { id: 5, title: "Obtention", icon: Hand },
    { id: 6, title: "Retrait", icon: XCircle },
    { id: 7, title: "Validité", icon: Clock },
  ];

  // ============================================
  // CHARGEMENT
  // ============================================
  useEffect(() => {
    if (entrepriseId) {
      loadData();
    } else {
      setIsLoading(false);
    }
  }, [entrepriseId]);

  const loadData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await loadConsentementsFromDB(entrepriseId);
      setConsentements(data);
    } catch (err) {
      setError("Erreur lors du chargement des consentements.");
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  // ============================================
  // FILTRAGE
  // ============================================
  const filteredConsentements = consentements.filter((c) => {
    const matchSearch =
      (c.nom || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (c.description || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (c.finalite_principale || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
    const matchActif =
      filterActif === "all" || (filterActif === "actif" ? c.actif : !c.actif);
    const matchType =
      filterType === "all" || c.type_consentement === filterType;
    return matchSearch && matchActif && matchType;
  });

  // Stats
  const stats = {
    total: consentements.length,
    actifs: consentements.filter((c) => c.actif).length,
    inactifs: consentements.filter((c) => !c.actif).length,
    explicites: consentements.filter((c) => c.consentement_explicite).length,
    sensibles: consentements.filter((c) => c.donnees_sensibles).length,
    aRenouveler: consentements.filter((c) => {
      if (!c.date_expiration) return false;
      const exp = new Date(c.date_expiration);
      const now = new Date();
      const diff = (exp - now) / (1000 * 60 * 60 * 24);
      return diff <= 30 && diff > 0;
    }).length,
  };

  // ============================================
  // HANDLERS
  // ============================================
  const handleAdd = () => {
    setEditingConsentement(null);
    setFormData(initialFormData);
    setActiveTab(0);
    setShowModal(true);
  };

  const handleEdit = (consentement) => {
    setEditingConsentement(consentement);
    setFormData({
      ...initialFormData,
      ...consentement,
      categories_donnees: consentement.categories_donnees || [],
      mecanismes_retrait: consentement.mecanismes_retrait || [],
    });
    setActiveTab(0);
    setShowModal(true);
  };

  const toggleArrayItem = (field, item) => {
    const current = formData[field] || [];
    if (current.includes(item)) {
      setFormData({ ...formData, [field]: current.filter((i) => i !== item) });
    } else {
      setFormData({ ...formData, [field]: [...current, item] });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.nom?.trim()) {
      showToast?.("Le nom du consentement est obligatoire.", "error");
      return;
    }

    if (!entrepriseId) {
      showToast?.("Erreur: Aucune entreprise sélectionnée.", "error");
      return;
    }

    // Validation Loi 25: données sensibles = consentement explicite
    if (formData.donnees_sensibles && !formData.consentement_explicite) {
      showToast?.(
        "Les données sensibles requièrent un consentement explicite (Loi 25).",
        "error"
      );
      return;
    }

    setIsSaving(true);
    try {
      const toNull = (val) =>
        val && typeof val === "string" && val.trim() !== "" ? val.trim() : null;

      const dataToSave = {
        nom: formData.nom.trim(),
        type_consentement: formData.type_consentement,
        description: toNull(formData.description),
        finalite_principale: toNull(formData.finalite_principale),
        finalites_secondaires: toNull(formData.finalites_secondaires),
        categories_donnees: formData.categories_donnees || [],
        donnees_sensibles: formData.donnees_sensibles || false,
        description_donnees: toNull(formData.description_donnees),
        consentement_explicite: formData.consentement_explicite || false,
        consentement_separe: formData.consentement_separe || false,
        consentement_granulaire: formData.consentement_granulaire || false,
        info_finalites: formData.info_finalites || false,
        info_categories_donnees: formData.info_categories_donnees || false,
        info_destinataires: formData.info_destinataires || false,
        info_duree_conservation: formData.info_duree_conservation || false,
        info_droits: formData.info_droits || false,
        info_transfert_hors_qc: formData.info_transfert_hors_qc || false,
        texte_information: toNull(formData.texte_information),
        methode_obtention: formData.methode_obtention,
        date_obtention: toNull(formData.date_obtention),
        preuve_consentement: toNull(formData.preuve_consentement),
        preuve_url: toNull(formData.preuve_url),
        version: toNull(formData.version) || "1.0",
        date_version: toNull(formData.date_version),
        changelog: toNull(formData.changelog),
        mecanismes_retrait: formData.mecanismes_retrait || [],
        delai_traitement_retrait: toNull(formData.delai_traitement_retrait),
        duree_validite: toNull(formData.duree_validite),
        date_expiration: toNull(formData.date_expiration),
        renouvellement_requis: formData.renouvellement_requis || false,
        frequence_renouvellement: toNull(formData.frequence_renouvellement),
        actif: formData.actif,
        obligatoire: formData.obligatoire || false,
        responsable: toNull(formData.responsable),
        notes: toNull(formData.notes),
        entreprise_id: entrepriseId,
      };

      if (editingConsentement) {
        const updated = await updateConsentementInDB(
          editingConsentement.id,
          dataToSave
        );
        setConsentements(
          consentements.map((c) =>
            c.id === editingConsentement.id ? updated : c
          )
        );
        showToast?.("Consentement modifié avec succès!", "success");
      } else {
        const newConsentement = await addConsentementToDB(dataToSave);
        setConsentements([newConsentement, ...consentements]);
        showToast?.("Consentement créé avec succès!", "success");
      }
      setShowModal(false);
    } catch (err) {
      console.error("Erreur détaillée:", err);
      showToast?.(
        `Erreur: ${err.message || "Erreur lors de l'enregistrement."}`,
        "error"
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteConsentementFromDB(id);
      setConsentements(consentements.filter((c) => c.id !== id));
      showToast?.("Consentement supprimé.", "success");
    } catch (err) {
      showToast?.("Erreur lors de la suppression.", "error");
    }
    setShowDeleteConfirm(null);
  };

  const toggleActif = async (consentement) => {
    try {
      const updated = await updateConsentementInDB(consentement.id, {
        actif: !consentement.actif,
      });
      setConsentements(
        consentements.map((c) => (c.id === consentement.id ? updated : c))
      );
      showToast?.(
        `Consentement ${updated.actif ? "activé" : "désactivé"}.`,
        "success"
      );
    } catch (err) {
      showToast?.("Erreur lors de la mise à jour.", "error");
    }
  };

  // ============================================
  // RENDU
  // ============================================
  if (!entrepriseId) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="mx-auto text-amber-500 mb-4" size={48} />
        <p className="text-gray-600 dark:text-gray-400">
          Veuillez sélectionner une entreprise.
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={40} className="text-green-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
            <CheckSquare className="text-green-600" />
            Gestion des consentements
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Article 14 de la Loi 25 — Consentement libre, éclairé et spécifique
          </p>
        </div>
        <button
          onClick={handleAdd}
          className="flex items-center gap-2 px-5 py-2.5 bg-green-600 text-white rounded-xl hover:bg-green-700 transition font-medium shadow-lg"
        >
          <Plus size={20} />
          Nouveau consentement
        </button>
      </div>

      {/* Alerte consentements à renouveler */}
      {stats.aRenouveler > 0 && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-500 p-4 rounded-r-lg">
          <div className="flex items-center gap-3">
            <Clock className="text-amber-600 flex-shrink-0" size={24} />
            <div>
              <p className="font-semibold text-amber-800 dark:text-amber-300">
                {stats.aRenouveler} consentement(s) expire(nt) bientôt
              </p>
              <p className="text-sm text-amber-700 dark:text-amber-400">
                Pensez à renouveler les consentements avant leur expiration.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm border border-gray-200 dark:border-gray-700">
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            {stats.total}
          </p>
          <p className="text-xs text-gray-500">Total</p>
        </div>
        <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 border border-green-200 dark:border-green-800">
          <p className="text-2xl font-bold text-green-700 dark:text-green-400">
            {stats.actifs}
          </p>
          <p className="text-xs text-green-600">Actifs</p>
        </div>
        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
          <p className="text-2xl font-bold text-gray-700 dark:text-gray-300">
            {stats.inactifs}
          </p>
          <p className="text-xs text-gray-500">Inactifs</p>
        </div>
        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
          <p className="text-2xl font-bold text-blue-700 dark:text-blue-400">
            {stats.explicites}
          </p>
          <p className="text-xs text-blue-600">Explicites</p>
        </div>
        <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4 border border-purple-200 dark:border-purple-800">
          <p className="text-2xl font-bold text-purple-700 dark:text-purple-400">
            {stats.sensibles}
          </p>
          <p className="text-xs text-purple-600">Données sensibles</p>
        </div>
        <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4 border border-amber-200 dark:border-amber-800">
          <p className="text-2xl font-bold text-amber-700 dark:text-amber-400">
            {stats.aRenouveler}
          </p>
          <p className="text-xs text-amber-600">À renouveler</p>
        </div>
      </div>

      {/* Filtres */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            size={18}
          />
          <input
            type="text"
            placeholder="Rechercher..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-xl"
          />
        </div>
        <select
          value={filterActif}
          onChange={(e) => setFilterActif(e.target.value)}
          className="px-4 py-2.5 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-xl"
        >
          <option value="all">Tous</option>
          <option value="actif">Actifs</option>
          <option value="inactif">Inactifs</option>
        </select>
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="px-4 py-2.5 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-xl"
        >
          <option value="all">Tous les types</option>
          {TYPES_CONSENTEMENT.map((t) => (
            <option key={t.id} value={t.id}>
              {t.label}
            </option>
          ))}
        </select>
        <button
          onClick={loadData}
          className="p-2.5 border border-gray-300 dark:border-gray-600 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700"
        >
          <RefreshCw size={20} className="text-gray-600 dark:text-gray-300" />
        </button>
      </div>

      {/* Liste */}
      {error ? (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-6 text-center">
          <AlertTriangle className="text-red-500 mx-auto mb-2" size={32} />
          <p className="text-red-700 dark:text-red-300">{error}</p>
        </div>
      ) : filteredConsentements.length === 0 ? (
        <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-12 text-center">
          <CheckSquare className="text-gray-400 mx-auto mb-4" size={48} />
          <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">
            Aucun consentement configuré
          </h3>
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            Configurez vos formulaires de consentement pour être conforme à la
            Loi 25.
          </p>
          <button
            onClick={handleAdd}
            className="px-5 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            <Plus size={18} className="inline mr-2" />
            Nouveau consentement
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredConsentements.map((consentement) => {
            const typeInfo = TYPES_CONSENTEMENT.find(
              (t) => t.id === consentement.type_consentement
            );
            const isExpiringSoon =
              consentement.date_expiration &&
              (new Date(consentement.date_expiration) - new Date()) /
                (1000 * 60 * 60 * 24) <=
                30;

            return (
              <div
                key={consentement.id}
                className={`bg-white dark:bg-gray-800 rounded-xl shadow-md border overflow-hidden ${
                  !consentement.actif
                    ? "border-gray-300 dark:border-gray-600 opacity-75"
                    : isExpiringSoon
                    ? "border-amber-300 dark:border-amber-700"
                    : "border-gray-200 dark:border-gray-700"
                }`}
              >
                <div className="p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 flex-wrap mb-2">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                          {consentement.nom || typeInfo?.label || "Sans nom"}
                        </h3>
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded-full ${
                            consentement.actif
                              ? "bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300"
                              : "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300"
                          }`}
                        >
                          {consentement.actif ? "Actif" : "Inactif"}
                        </span>
                        {consentement.consentement_explicite && (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300">
                            Explicite
                          </span>
                        )}
                        {consentement.donnees_sensibles && (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-300">
                            Données sensibles
                          </span>
                        )}
                        {consentement.obligatoire && (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300">
                            Obligatoire
                          </span>
                        )}
                        {isExpiringSoon && (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300 flex items-center gap-1">
                            <Clock size={12} />
                            Expire bientôt
                          </span>
                        )}
                      </div>

                      {consentement.finalite_principale && (
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                          <strong>Finalité:</strong>{" "}
                          {consentement.finalite_principale}
                        </p>
                      )}

                      <div className="flex flex-wrap gap-4 text-sm text-gray-500 dark:text-gray-400">
                        <span className="flex items-center gap-1">
                          <FileText size={14} />
                          {typeInfo?.label || consentement.type_consentement}
                        </span>
                        {consentement.version && (
                          <span className="flex items-center gap-1">
                            <History size={14} />v{consentement.version}
                          </span>
                        )}
                        {consentement.categories_donnees?.length > 0 && (
                          <span className="flex items-center gap-1">
                            <Database size={14} />
                            {consentement.categories_donnees.length}{" "}
                            catégorie(s)
                          </span>
                        )}
                        {consentement.mecanismes_retrait?.length > 0 && (
                          <span className="flex items-center gap-1 text-green-600">
                            <XCircle size={14} />
                            Retrait configuré
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => toggleActif(consentement)}
                        className={`p-2 rounded-lg transition ${
                          consentement.actif
                            ? "text-green-600 hover:bg-green-50 dark:hover:bg-green-900/30"
                            : "text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                        }`}
                        title={consentement.actif ? "Désactiver" : "Activer"}
                      >
                        {consentement.actif ? (
                          <ToggleRight size={24} />
                        ) : (
                          <ToggleLeft size={24} />
                        )}
                      </button>
                      <button
                        onClick={() =>
                          setShowDetail(
                            showDetail === consentement.id
                              ? null
                              : consentement.id
                          )
                        }
                        className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg"
                      >
                        <Eye size={18} />
                      </button>
                      <button
                        onClick={() => handleEdit(consentement)}
                        className="p-2 text-gray-500 hover:text-sky-600 hover:bg-sky-50 dark:hover:bg-sky-900/30 rounded-lg"
                      >
                        <Edit2 size={18} />
                      </button>
                      <button
                        onClick={() => setShowDeleteConfirm(consentement.id)}
                        className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>

                  {/* Détails */}
                  {showDetail === consentement.id && (
                    <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 grid md:grid-cols-2 gap-4 text-sm">
                      {consentement.methode_obtention && (
                        <div>
                          <span className="font-medium text-gray-700 dark:text-gray-300">
                            Méthode:
                          </span>
                          <p className="text-gray-600 dark:text-gray-400">
                            {METHODES_OBTENTION.find(
                              (m) => m.id === consentement.methode_obtention
                            )?.label || consentement.methode_obtention}
                          </p>
                        </div>
                      )}
                      {consentement.categories_donnees?.length > 0 && (
                        <div>
                          <span className="font-medium text-gray-700 dark:text-gray-300">
                            Données:
                          </span>
                          <p className="text-gray-600 dark:text-gray-400">
                            {consentement.categories_donnees.join(", ")}
                          </p>
                        </div>
                      )}
                      {consentement.mecanismes_retrait?.length > 0 && (
                        <div className="md:col-span-2">
                          <span className="font-medium text-gray-700 dark:text-gray-300">
                            Mécanismes de retrait:
                          </span>
                          <p className="text-gray-600 dark:text-gray-400">
                            {consentement.mecanismes_retrait.join(", ")}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Confirmation suppression */}
                {showDeleteConfirm === consentement.id && (
                  <div className="bg-red-50 dark:bg-red-900/20 p-4 border-t border-red-200 dark:border-red-800">
                    <p className="text-red-700 dark:text-red-300 mb-3">
                      Supprimer ce consentement?
                    </p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleDelete(consentement.id)}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                      >
                        Supprimer
                      </button>
                      <button
                        onClick={() => setShowDeleteConfirm(null)}
                        className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg"
                      >
                        Annuler
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ============================================ */}
      {/* MODAL FORMULAIRE */}
      {/* ============================================ */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden shadow-2xl flex flex-col">
            {/* Header */}
            <div className="px-6 py-4 border-b dark:border-gray-700 flex items-center justify-between bg-green-50 dark:bg-green-900/20">
              <div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                  {editingConsentement
                    ? "Modifier le consentement"
                    : "Nouveau consentement"}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Consentement libre, éclairé et spécifique (Art. 14)
                </p>
              </div>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg"
              >
                <X size={24} />
              </button>
            </div>

            {/* Navigation */}
            <div className="px-4 py-3 border-b dark:border-gray-700 overflow-x-auto">
              <div className="flex gap-1 min-w-max">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition ${
                      activeTab === tab.id
                        ? "bg-green-600 text-white"
                        : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200"
                    }`}
                  >
                    <tab.icon size={16} />
                    <span className="hidden md:inline">{tab.title}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Contenu */}
            <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
              <div className="p-6 space-y-6">
                {/* TAB 0: IDENTIFICATION */}
                {activeTab === 0 && (
                  <div className="space-y-4">
                    <h4 className="font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-2">
                      <FileText size={20} className="text-green-600" />
                      Identification
                    </h4>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Nom du consentement *
                        </label>
                        <input
                          type="text"
                          value={formData.nom}
                          onChange={(e) =>
                            setFormData({ ...formData, nom: e.target.value })
                          }
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                          placeholder="Ex: Consentement newsletter"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Type de consentement
                        </label>
                        <select
                          value={formData.type_consentement}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              type_consentement: e.target.value,
                            })
                          }
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                        >
                          {TYPES_CONSENTEMENT.map((t) => (
                            <option key={t.id} value={t.id}>
                              {t.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Description
                      </label>
                      <textarea
                        value={formData.description}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            description: e.target.value,
                          })
                        }
                        rows={3}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                        placeholder="Description du consentement..."
                      />
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <label className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.actif}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              actif: e.target.checked,
                            })
                          }
                          className="w-5 h-5 rounded border-gray-300 text-green-600"
                        />
                        <div>
                          <span className="font-medium text-gray-700 dark:text-gray-300">
                            Actif
                          </span>
                          <p className="text-xs text-gray-500">
                            Ce consentement est actuellement utilisé
                          </p>
                        </div>
                      </label>
                      <label className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.obligatoire}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              obligatoire: e.target.checked,
                            })
                          }
                          className="w-5 h-5 rounded border-gray-300 text-red-600"
                        />
                        <div>
                          <span className="font-medium text-gray-700 dark:text-gray-300">
                            Obligatoire
                          </span>
                          <p className="text-xs text-gray-500">
                            Requis pour utiliser le service
                          </p>
                        </div>
                      </label>
                    </div>
                  </div>
                )}

                {/* TAB 1: FINALITÉ */}
                {activeTab === 1 && (
                  <div className="space-y-4">
                    <h4 className="font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-2">
                      <Target size={20} className="text-green-600" />
                      Finalité du traitement (Art. 14)
                    </h4>

                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                      <p className="text-sm text-blue-800 dark:text-blue-300">
                        <strong>Loi 25:</strong> Le consentement doit être donné
                        pour une finalité spécifique et clairement définie.
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Finalité principale *
                      </label>
                      <textarea
                        value={formData.finalite_principale}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            finalite_principale: e.target.value,
                          })
                        }
                        rows={2}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                        placeholder="Ex: Envoi d'offres promotionnelles personnalisées par courriel"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Finalités secondaires (le cas échéant)
                      </label>
                      <textarea
                        value={formData.finalites_secondaires}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            finalites_secondaires: e.target.value,
                          })
                        }
                        rows={2}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                        placeholder="Autres usages des données..."
                      />
                    </div>
                  </div>
                )}

                {/* TAB 2: DONNÉES */}
                {activeTab === 2 && (
                  <div className="space-y-4">
                    <h4 className="font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-2">
                      <Database size={20} className="text-green-600" />
                      Données concernées
                    </h4>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Catégories de données
                      </label>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {CATEGORIES_DONNEES.map((cat) => (
                          <label
                            key={cat}
                            className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-700 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                          >
                            <input
                              type="checkbox"
                              checked={formData.categories_donnees?.includes(
                                cat
                              )}
                              onChange={() =>
                                toggleArrayItem("categories_donnees", cat)
                              }
                              className="rounded border-gray-300 text-green-600"
                            />
                            <span className="text-sm text-gray-700 dark:text-gray-300">
                              {cat}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <label className="flex items-center gap-3 p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg cursor-pointer border-l-4 border-purple-500">
                      <input
                        type="checkbox"
                        checked={formData.donnees_sensibles}
                        onChange={(e) => {
                          setFormData({
                            ...formData,
                            donnees_sensibles: e.target.checked,
                            consentement_explicite: e.target.checked
                              ? true
                              : formData.consentement_explicite,
                          });
                        }}
                        className="w-5 h-5 rounded border-gray-300 text-purple-600"
                      />
                      <div>
                        <span className="font-medium text-purple-800 dark:text-purple-300">
                          Données sensibles (Art. 12)
                        </span>
                        <p className="text-xs text-purple-700 dark:text-purple-400">
                          Active automatiquement le consentement explicite
                        </p>
                      </div>
                    </label>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Description des données
                      </label>
                      <textarea
                        value={formData.description_donnees}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            description_donnees: e.target.value,
                          })
                        }
                        rows={2}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                        placeholder="Description détaillée des données collectées..."
                      />
                    </div>
                  </div>
                )}

                {/* TAB 3: CARACTÉRISTIQUES */}
                {activeTab === 3 && (
                  <div className="space-y-4">
                    <h4 className="font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-2">
                      <CheckSquare size={20} className="text-green-600" />
                      Caractéristiques du consentement (Art. 14)
                    </h4>

                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                      <p className="text-sm text-blue-800 dark:text-blue-300">
                        <strong>Loi 25:</strong> Le consentement doit être
                        libre, éclairé, spécifique et donné à des fins
                        spécifiques.
                      </p>
                    </div>

                    <div className="space-y-3">
                      <label
                        className={`flex items-center gap-3 p-4 rounded-lg cursor-pointer ${
                          formData.donnees_sensibles
                            ? "bg-purple-100 dark:bg-purple-900/30 border-2 border-purple-500"
                            : "bg-gray-50 dark:bg-gray-700"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={formData.consentement_explicite}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              consentement_explicite: e.target.checked,
                            })
                          }
                          className="w-5 h-5 rounded border-gray-300 text-green-600"
                          disabled={formData.donnees_sensibles}
                        />
                        <div>
                          <span className="font-medium text-gray-700 dark:text-gray-300">
                            Consentement explicite
                          </span>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            Action positive et non ambiguë (case à cocher,
                            signature, etc.)
                          </p>
                          {formData.donnees_sensibles && (
                            <p className="text-xs text-purple-600 dark:text-purple-400 mt-1">
                              ⚠️ Obligatoire pour les données sensibles
                            </p>
                          )}
                        </div>
                      </label>

                      <label className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.consentement_separe}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              consentement_separe: e.target.checked,
                            })
                          }
                          className="w-5 h-5 rounded border-gray-300 text-green-600"
                        />
                        <div>
                          <span className="font-medium text-gray-700 dark:text-gray-300">
                            Consentement séparé
                          </span>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            Présenté distinctement des autres conditions (CGU,
                            etc.)
                          </p>
                        </div>
                      </label>

                      <label className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.consentement_granulaire}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              consentement_granulaire: e.target.checked,
                            })
                          }
                          className="w-5 h-5 rounded border-gray-300 text-green-600"
                        />
                        <div>
                          <span className="font-medium text-gray-700 dark:text-gray-300">
                            Consentement granulaire
                          </span>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            La personne peut choisir finalité par finalité
                          </p>
                        </div>
                      </label>
                    </div>
                  </div>
                )}

                {/* TAB 4: INFORMATION */}
                {activeTab === 4 && (
                  <div className="space-y-4">
                    <h4 className="font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-2">
                      <Info size={20} className="text-green-600" />
                      Information fournie (Art. 8)
                    </h4>

                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                      <p className="text-sm text-blue-800 dark:text-blue-300">
                        <strong>Loi 25:</strong> La personne doit être informée
                        au moment de la collecte.
                      </p>
                    </div>

                    <div className="space-y-2">
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Information communiquée au moment du consentement:
                      </p>

                      {[
                        {
                          key: "info_finalites",
                          label: "Finalités du traitement",
                        },
                        {
                          key: "info_categories_donnees",
                          label: "Catégories de données collectées",
                        },
                        {
                          key: "info_destinataires",
                          label: "Destinataires des données",
                        },
                        {
                          key: "info_duree_conservation",
                          label: "Durée de conservation",
                        },
                        {
                          key: "info_droits",
                          label:
                            "Droits de la personne (accès, rectification, etc.)",
                        },
                        {
                          key: "info_transfert_hors_qc",
                          label: "Transfert hors Québec (le cas échéant)",
                        },
                      ].map((item) => (
                        <label
                          key={item.key}
                          className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={formData[item.key]}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                [item.key]: e.target.checked,
                              })
                            }
                            className="w-5 h-5 rounded border-gray-300 text-green-600"
                          />
                          <span className="text-gray-700 dark:text-gray-300">
                            {item.label}
                          </span>
                        </label>
                      ))}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Texte d'information présenté
                      </label>
                      <textarea
                        value={formData.texte_information}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            texte_information: e.target.value,
                          })
                        }
                        rows={4}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                        placeholder="Texte affiché à la personne au moment de demander son consentement..."
                      />
                    </div>
                  </div>
                )}

                {/* TAB 5: OBTENTION */}
                {activeTab === 5 && (
                  <div className="space-y-4">
                    <h4 className="font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-2">
                      <Hand size={20} className="text-green-600" />
                      Obtention du consentement
                    </h4>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Méthode d'obtention
                        </label>
                        <select
                          value={formData.methode_obtention}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              methode_obtention: e.target.value,
                            })
                          }
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                        >
                          {METHODES_OBTENTION.map((m) => (
                            <option key={m.id} value={m.id}>
                              {m.icone} {m.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Date de mise en place
                        </label>
                        <input
                          type="date"
                          value={formData.date_obtention}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              date_obtention: e.target.value,
                            })
                          }
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Preuve de consentement
                      </label>
                      <input
                        type="text"
                        value={formData.preuve_consentement}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            preuve_consentement: e.target.value,
                          })
                        }
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                        placeholder="Ex: Horodatage, capture d'écran, enregistrement..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        URL du formulaire/politique
                      </label>
                      <input
                        type="url"
                        value={formData.preuve_url}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            preuve_url: e.target.value,
                          })
                        }
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                        placeholder="https://..."
                      />
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Version du formulaire
                        </label>
                        <input
                          type="text"
                          value={formData.version}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              version: e.target.value,
                            })
                          }
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                          placeholder="Ex: 1.0, 2.1"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Date de version
                        </label>
                        <input
                          type="date"
                          value={formData.date_version}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              date_version: e.target.value,
                            })
                          }
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* TAB 6: RETRAIT */}
                {activeTab === 6 && (
                  <div className="space-y-4">
                    <h4 className="font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-2">
                      <XCircle size={20} className="text-green-600" />
                      Mécanismes de retrait (Art. 14)
                    </h4>

                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                      <p className="text-sm text-blue-800 dark:text-blue-300">
                        <strong>Loi 25:</strong> Le consentement peut être
                        retiré à tout moment. Le retrait doit être aussi simple
                        que l'obtention.
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Mécanismes de retrait disponibles
                      </label>
                      <div className="space-y-2">
                        {MECANISMES_RETRAIT.map((mec) => (
                          <label
                            key={mec}
                            className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                          >
                            <input
                              type="checkbox"
                              checked={formData.mecanismes_retrait?.includes(
                                mec
                              )}
                              onChange={() =>
                                toggleArrayItem("mecanismes_retrait", mec)
                              }
                              className="w-5 h-5 rounded border-gray-300 text-green-600"
                            />
                            <span className="text-gray-700 dark:text-gray-300">
                              {mec}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Délai de traitement du retrait
                      </label>
                      <input
                        type="text"
                        value={formData.delai_traitement_retrait}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            delai_traitement_retrait: e.target.value,
                          })
                        }
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                        placeholder="Ex: 72 heures, 10 jours ouvrables"
                      />
                    </div>
                  </div>
                )}

                {/* TAB 7: VALIDITÉ */}
                {activeTab === 7 && (
                  <div className="space-y-4">
                    <h4 className="font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-2">
                      <Clock size={20} className="text-green-600" />
                      Durée de validité
                    </h4>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Durée de validité
                        </label>
                        <input
                          type="text"
                          value={formData.duree_validite}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              duree_validite: e.target.value,
                            })
                          }
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                          placeholder="Ex: 1 an, 2 ans, illimité"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Date d'expiration
                        </label>
                        <input
                          type="date"
                          value={formData.date_expiration}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              date_expiration: e.target.value,
                            })
                          }
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                        />
                      </div>
                    </div>

                    <label className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.renouvellement_requis}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            renouvellement_requis: e.target.checked,
                          })
                        }
                        className="w-5 h-5 rounded border-gray-300 text-green-600"
                      />
                      <div>
                        <span className="font-medium text-gray-700 dark:text-gray-300">
                          Renouvellement requis
                        </span>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Le consentement doit être redemandé périodiquement
                        </p>
                      </div>
                    </label>

                    {formData.renouvellement_requis && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Fréquence de renouvellement
                        </label>
                        <input
                          type="text"
                          value={formData.frequence_renouvellement}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              frequence_renouvellement: e.target.value,
                            })
                          }
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                          placeholder="Ex: Annuel, tous les 2 ans"
                        />
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Responsable
                      </label>
                      <input
                        type="text"
                        value={formData.responsable}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            responsable: e.target.value,
                          })
                        }
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                        placeholder="Personne responsable de ce consentement"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Notes
                      </label>
                      <textarea
                        value={formData.notes}
                        onChange={(e) =>
                          setFormData({ ...formData, notes: e.target.value })
                        }
                        rows={3}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                        placeholder="Notes additionnelles..."
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t dark:border-gray-700 bg-gray-50 dark:bg-gray-900 flex items-center justify-between">
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setActiveTab(Math.max(0, activeTab - 1))}
                    disabled={activeTab === 0}
                    className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg disabled:opacity-50"
                  >
                    ← Précédent
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setActiveTab(Math.min(tabs.length - 1, activeTab + 1))
                    }
                    disabled={activeTab === tabs.length - 1}
                    className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg disabled:opacity-50"
                  >
                    Suivant →
                  </button>
                </div>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-5 py-2 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    disabled={isSaving}
                    className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
                  >
                    {isSaving ? (
                      <>
                        <Loader2 size={18} className="animate-spin" />
                        Enregistrement...
                      </>
                    ) : (
                      <>
                        <CheckCircle size={18} />
                        {editingConsentement ? "Modifier" : "Créer"}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConsentementsManager;
